import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PillGroup } from "../components/ui/Form";
import CustomDatePicker from "../components/ui/DatePicker";
import NavBar from "../components/NavBar/NavBar.jsx";
import Footer from "../components/Footer/Footer.jsx";
import useAuthStore from "../Store/AuthStore";
import { fetchProfileAPI, updateProfileAPI } from "../Services/userService";
import Loader from "../components/Loader/Loader";
import { INTERESTS, PERSONALITY_TRAITS, mapToPlain, mapToEmojiFormat, trimSpaces, getInterestOptionsFromApi, getPersonalityOptionsFromApi } from "../Utils/personaUtils";

const SettingsPage = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { isAuthenticated } = useAuthStore();
    
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [profileData, setProfileData] = useState({
        name: "",
        dob: "",
        gender: "",
        interests: [],
        personality: []
    });
    const [originalData, setOriginalData] = useState({});

    const genderOptions = useMemo(() => [
        { value: "Male", label: t("gender.male") },
        { value: "Female", label: t("gender.female") },
        { value: "Non Binary", label: t("gender.nonBinary") },
    ], [t]);

    const [interestOptions, setInterestOptions] = useState([]);
    const [personalityOptions, setPersonalityOptions] = useState([]);

    useEffect(() => {
        if (!isAuthenticated) {
            history.push("/login");
            return;
        }

        const fetchUserProfile = async () => {
            setIsLoading(true);
            try {
                const response = await fetchProfileAPI();
                const userDetails = response?.["user-details"] || response;

                if (userDetails && !userDetails.error) {
                    const hasNameData = !!userDetails.name;
                    const hasDobData = !!userDetails.dob;

                    const apiInterests = userDetails.interests || [];
                    const apiPersonality = userDetails.personality || [];

                    setInterestOptions(getInterestOptionsFromApi(apiInterests, t));
                    setPersonalityOptions(getPersonalityOptionsFromApi(apiPersonality, t));

                    const data = {
                        name: userDetails.name || "",
                        dob: userDetails.dob || "",
                        gender: userDetails.gender || "",
                        interests: mapToEmojiFormat(apiInterests, INTERESTS),
                        personality: mapToEmojiFormat(apiPersonality, PERSONALITY_TRAITS),
                        _hasNameData: hasNameData,
                        _hasDobData: hasDobData
                    };

                    setProfileData(data);
                    setOriginalData({
                        name: userDetails.name || "",
                        dob: userDetails.dob || "",
                        gender: userDetails.gender || "",
                        interests: apiInterests,
                        personality: apiPersonality.map(p => trimSpaces(p))
                    });
                }
            } catch (error) {
                console.error("Failed to fetch profile:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserProfile();
    }, [isAuthenticated, history, t]);

    useEffect(() => {
        const interests = profileData.interests || [];
        const personality = profileData.personality || [];
        
        setInterestOptions(getInterestOptionsFromApi(interests, t));
        setPersonalityOptions(getPersonalityOptionsFromApi(personality, t));
    }, [profileData.interests, profileData.personality, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        
        try {
            const dataToUpdate = {
                name: profileData.name,
                dob: profileData.dob,
                gender: profileData.gender || undefined,
                interests: mapToPlain(profileData.interests),
                personality: mapToPlain(profileData.personality)
            };
            
            await updateProfileAPI(dataToUpdate);
            
            setOriginalData({
                name: profileData.name,
                dob: profileData.dob,
                gender: profileData.gender,
                interests: mapToPlain(profileData.interests),
                personality: mapToPlain(profileData.personality)
            });
            
            setProfileData(prev => ({
                ...prev,
                _hasNameData: !!profileData.name,
                _hasDobData: !!profileData.dob
            }));
            
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
        } catch (error) {
            console.error("Failed to update profile:", error);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <>
                <NavBar />
                <div className="flex justify-center items-center min-h-screen pt-20">
                    <Loader />
                </div>
                <Footer />
            </>
        );
    }

    const hasNameData = profileData._hasNameData;
    const hasDobData = profileData._hasDobData;

    return (
        <>
            <NavBar />
            <div className="min-h-screen bg-white pt-24 pb-12 px-4">
                <div className="max-w-2xl mx-auto">
                    <div className="bg-grey-500 rounded-3xl shadow-lg p-6 md:p-8">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-black">
                                {t("settings.title", "Settings")}
                            </h1>
                            <p className="text-neutral-green-600 mt-2">
                                {t("settings.subtitle", "Manage your profile and preferences")}
                            </p>
                        </div>

                        {showSuccess && (
                            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-full text-green-700 text-center">
                                {t("settings.success", "Settings saved successfully!")}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {hasNameData ? (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-neutral-green-700">
                                        {t("profile.name", "Name")}
                                    </label>
                                    <input
                                        type="text"
                                        value={profileData.name}
                                        disabled
                                        readOnly
                                        className="block w-full px-3 py-2 border border-neutral-200 rounded-full bg-neutral-100 text-neutral-500 cursor-not-allowed"
                                    />
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-neutral-green-700">
                                        {t("profile.name", "Name")}
                                    </label>
                                    <input
                                        type="text"
                                        value={profileData.name}
                                        onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder={t("settings.enterName", "Enter your name")}
                                        className="block w-full px-3 py-2 border border-neutral-300 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                    />
                                </div>
                            )}

                            {hasDobData ? (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-neutral-green-700">
                                        {t("profile.dob", "Date of Birth")}
                                    </label>
                                    <input
                                        type="text"
                                        value={profileData.dob ? new Date(profileData.dob).toLocaleDateString() : ""}
                                        disabled
                                        readOnly
                                        className="block w-full px-3 py-2 border border-neutral-200 rounded-full bg-neutral-100 text-neutral-500 cursor-not-allowed"
                                    />
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-neutral-green-700">
                                        {t("profile.dob", "Date of Birth")}
                                    </label>
                                    <CustomDatePicker
                                        selected={profileData.dob ? new Date(profileData.dob) : null}
                                        onChange={(date) => setProfileData(prev => ({ ...prev, dob: date ? date.toISOString().split('T')[0] : '' }))}
                                        maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 10))}
                                        minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 100))}
                                        placeholderText={t("profile.dob", "Select date of birth")}
                                        className="block w-full px-3 py-2 border border-neutral-300 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                        dateFormat="yyyy-MM-dd"
                                        showTimeSelect={false}
                                    />
                                </div>
                            )}

                            <div className="flex flex-col gap-2.5">
                                <label className="text-sm font-medium text-neutral-green-700">
                                    {t("profile.gender", "Gender")}
                                </label>
                                <PillGroup
                                    options={genderOptions}
                                    selected={profileData.gender}
                                    onSelect={(val) => setProfileData(prev => ({ ...prev, gender: val }))}
                                    multiple={false}
                                    size="xs2"
                                />
                            </div>

                            <div className="space-y-4 pt-4 border-t border-neutral-100">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-neutral-green-700">
                                        {t("profile.interests", "Interests and Hobbies")}
                                    </label>
                                    <PillGroup
                                        options={interestOptions}
                                        selected={profileData.interests}
                                        onSelect={(val) => setProfileData(prev => ({ ...prev, interests: val }))}
                                        multiple
                                        size="xs2"
                                    />
                                </div>
                            </div>

                            <div className="space-y-4 pt-4 border-t border-neutral-100">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-neutral-green-700">
                                        {t("profile.personality", "Personality")}
                                    </label>
                                    <PillGroup
                                        options={personalityOptions}
                                        selected={profileData.personality}
                                        onSelect={(val) => setProfileData(prev => ({ ...prev, personality: val }))}
                                        multiple
                                        size="xs2"
                                    />
                                </div>
                            </div>

                            <div className="pt-6">
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="w-full py-3 px-6 bg-red-500 hover:bg-red-600 text-white font-bold rounded-full shadow-lg shadow-red-100 transition-all duration-300 disabled:opacity-50"
                                >
                                    {isSaving ? t("common.saving", "Saving...") : t("settings.save", "Save Settings")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default SettingsPage;
